import { memo, useMemo } from "react";
import PropTypes from "prop-types";
import { Handle, Position, NodeResizer } from "@xyflow/react";
import { useSelector } from "react-redux";
import { useGetDialDataQuery, useGetChartDataQuery } from "@/redux/api/dashboardApi";
import { useLiveDataWindow } from "../utils/useLiveDataWindow";
import { Spin } from "antd";
import GaugeWidget from "../widgets/GaugeWidget";
import MiniGraphWidget from "../widgets/MiniGraphWidget";
import ValueDisplayWidget from "../widgets/ValueDisplayWidget";

/**
 * VisualizationWidgetNode – A standalone ReactFlow node that renders
 * a floating SCADA visualization widget (Gauge / MiniGraph / Value).
 *
 * This node is auto-created from parent node's `visualizationWidget` config
 * and linked via a `widgetConnector` edge.
 *
 * Props via data:
 * - parentNodeId: the parent SCADA node this widget belongs to
 * - widgetType: "gauge" | "graph" | "value"
 * - deviceId / parameterId: device binding (inherited from parent)
 * - config: { min, max, unit, color, warningThreshold, criticalThreshold }
 * - title: widget label
 */
const VisualizationWidgetNode = memo(({ data, selected, width, height }) => {
  const widgetType = data?.widgetType || "value";
  const deviceId = data?.deviceId;
  const parameterId = data?.parameterId;
  const config = data?.config || {};
  const title = data?.title || "";
  const color = config.color || "#3b82f6";
  const minValue = config.minValue ?? 0;
  const maxValue = config.maxValue ?? 100;
  const unit = config.unit || "";

  const { dateRange } = useSelector((state) => state.dashboardV2 || { dateRange: {} });
  const liveWindow = useLiveDataWindow();

  // Build API params for live data fetch
  const params = useMemo(() => {
    if (!deviceId || !parameterId) return null;
    const date = liveWindow.value;
    return {
      ...date,
      device: deviceId,
      peakHours: dateRange?.peakHours,
      deviceParameter: JSON.stringify({ _id_in: parameterId, populate: "systemFormula systemParameter device" }),
    };
  }, [deviceId, parameterId, dateRange, liveWindow]);

  // Fetch live data with 10-second polling
  const { data: apiData, isFetching } = useGetDialDataQuery(params, {
    skip: !params,
    pollingInterval: 10000,
  });

  const chartParams = useMemo(() => {
    if (widgetType !== "graph" || !deviceId || !parameterId) return null;
    const date = liveWindow.today;
    return {
      ...date,
      device: deviceId,
      interval: "minute",
      peakHours: dateRange?.peakHours,
      deviceParameter: JSON.stringify({ _id_in: parameterId, populate: "systemFormula systemParameter device" }),
    };
  }, [widgetType, deviceId, parameterId, dateRange, liveWindow]);

  const { data: chartApiData, isFetching: isChartFetching } = useGetChartDataQuery(chartParams, {
    skip: !chartParams,
  });

  const chartHistory = chartApiData?.data?.[0]?.chart || [];

  const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;
  const rawValue = apiData?.data?.value;
  const rawDate = apiData?.data?.date;
  const dataTimestamp = rawDate ? new Date(rawDate).getTime() : null;
  const isWithin12Hours = dataTimestamp !== null && !Number.isNaN(dataTimestamp) && (Date.now() - dataTimestamp <= TWELVE_HOURS_MS);
  const hasValidValue = rawValue !== undefined && rawValue !== null && rawValue !== "" && rawValue !== "--" && !Number.isNaN(Number(rawValue));
  const hasLiveData = hasValidValue && isWithin12Hours;
  const val = hasLiveData ? Number(rawValue) : 0;

  // Render the appropriate widget
  const renderWidget = () => {
    if (!params) {
      return (
        <div className="scada-widget scada-widget--empty">
          <span className="text-muted" style={{ fontSize: 11 }}>No device bound</span>
        </div>
      );
    }

    if ((isFetching && !hasLiveData) || (widgetType === "graph" && isChartFetching && chartHistory.length === 0)) {
      return (
        <div className="scada-widget scada-widget--loading">
          <Spin size="small" />
        </div>
      );
    }

    switch (widgetType) {
      case "gauge":
        return (
          <GaugeWidget
            value={val}
            min={minValue}
            max={maxValue}
            unit={unit}
            color={color}
            label={title}
            width={width}
            height={height}
            greenStart={config.greenStart}
            greenEnd={config.greenEnd}
            yellowStart={config.yellowStart}
            yellowEnd={config.yellowEnd}
            redStart={config.redStart}
            redEnd={config.redEnd}
          />
        );
      case "graph":
        return (
          <MiniGraphWidget
            value={val}
            historyData={chartHistory}
            min={minValue}
            max={maxValue}
            unit={unit}
            color={color}
            label={title}
            warningThreshold={config.warningThreshold}
            criticalThreshold={config.criticalThreshold}
            width={width}
            height={height}
          />
        );
      case "value":
      default:
        return (
          <ValueDisplayWidget
            value={hasLiveData ? val : undefined}
            unit={unit}
            color={color}
            label={title}
            min={minValue}
            max={maxValue}
            width={width}
            height={height}
          />
        );
    }
  };

  return (
    <div className={`visualization-widget-node ${selected ? "visualization-widget-node--selected" : ""}`}>
      <NodeResizer
        isVisible={selected && !data?.readOnly}
        minWidth={60}
        minHeight={60}
        lineClassName="live-node__resize-line"
        handleClassName="live-node__resize-handle"
      />
      {/* Hidden dummy handle off-screen to satisfy React Flow edge bounds without intercepting user drags */}
      <Handle
        type="target"
        position={Position.Top}
        id="widget-input"
        isConnectable={false}
        style={{ position: 'absolute', top: -9999, left: -9999, opacity: 0, pointerEvents: 'none' }}
      />

      {renderWidget()}
    </div>
  );
});

VisualizationWidgetNode.displayName = "VisualizationWidgetNode";

VisualizationWidgetNode.propTypes = {
  data: PropTypes.shape({
    parentNodeId: PropTypes.string,
    widgetType: PropTypes.string,
    deviceId: PropTypes.string,
    parameterId: PropTypes.string,
    config: PropTypes.object,
    title: PropTypes.string,
    readOnly: PropTypes.bool,
  }),
  selected: PropTypes.bool,
  id: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default VisualizationWidgetNode;
